import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import { NotificationsNoneRounded } from '@mui/icons-material';
import { Popover } from '@mui/material';
import { notificationStore } from '@stores/Notifications';
import { observer } from 'mobx-react';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import css from './NotifBell.module.scss';
import { NotifPanelBody } from './NotifPanelBody';
import { useNotifActivity } from './useNotifActivity';

/**
 * General-purpose notifications bell (desktop). Hosts toasts that mirror with
 * `surfaces: ['toast', 'notif-bell']` — non-upload activity like media deletes,
 * downloads, library settings changes, moment exports, etc.
 *
 * Independent from the upload activity panel: separate popover, unread badge,
 * clear action. Both surfaces read the same `notificationStore` filtered by
 * type. The panel body + controller are shared with the mobile drawer
 * (see MobileNotifCenter).
 */
export const NotifBell = observer(() => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const { handlers, modals } = useNotifActivity();

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    notificationStore.setNotifBellPopoverOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    notificationStore.setNotifBellPopoverOpen(false);
  }, []);

  const unread = notificationStore.notifBellUnreadCount;

  return (
    <>
      <button
        ref={buttonRef}
        className={css.bellButton}
        aria-label="Notifications"
        onClick={handleClick}
      >
        <NotificationsNoneRounded />
        {unread > 0 && <span className={css.badge}>{unread}</span>}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          transition: frostedGlassTransitionProps,
          paper: {
            sx: {
              background: 'rgba(30, 30, 30, 0.85)',
              backdropFilter: 'blur(50px)',
              WebkitBackdropFilter: 'blur(50px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              boxShadow:
                '0 24px 32px -12px rgba(18, 18, 18, 0.6), 0 8px 24px -8px rgba(0, 0, 0, 0.4)',
              marginTop: '8px',
              overflow: 'hidden',
            },
          },
        }}
      >
        <NotifPanelBody handlers={handlers} />
      </Popover>
      {modals}
    </>
  );
});
